import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert } from '@components/toastify/notify-toast';
import { TASK_INSTANCES_LIMIT, TASK_INSTANCES_ORDER_BY } from '../../constants';
import { AirflowTaskInstance } from '../../types';

/*
 * Manages the DAG-task detail's Task Instances tab — every instance of this task across
 * all runs, server-paginated and state-filtered
 */
export function useTaskInstancesTab(dagId: string, taskId: string, refreshKey: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [instances, setInstances] = useState<AirflowTaskInstance[]>([]);
    const [totalEntries, setTotalEntries] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [stateFilter, setStateFilter] = useState('');
    const [offset, setOffset] = useState(0);

    const fetchInstances = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await AirflowDashboardHandler.listDagTaskInstances(Env, dagId, {
                limit: TASK_INSTANCES_LIMIT,
                offset,
                order_by: TASK_INSTANCES_ORDER_BY,
                taskId,
                state: stateFilter ? [stateFilter] : undefined,
            });
            setInstances(response.task_instances);
            setTotalEntries(response.total_entries ?? response.task_instances.length);
        } catch {
            errorAlert('Failed to load task instances.');
        } finally {
            setIsLoading(false);
        }
    }, [Env, dagId, taskId, offset, stateFilter]);

    useEffect(() => {
        fetchInstances();
    }, [fetchInstances, refreshKey]);

    const handleStateFilterChange = useCallback((value: string) => {
        setStateFilter(value);
        setOffset(0);
    }, []);

    const handlePrevPage = useCallback(() => setOffset((prev) => Math.max(0, prev - TASK_INSTANCES_LIMIT)), []);
    const handleNextPage = useCallback(() => setOffset((prev) => prev + TASK_INSTANCES_LIMIT), []);

    return { instances, totalEntries, isLoading, stateFilter, offset, handleStateFilterChange, handlePrevPage, handleNextPage };
}
